import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// The 4 namespaces needed for the very first paint (global chrome + Home)
// are bundled synchronously, for all 3 languages -- 12 files, ~25KB total.
// Everything else (prices, gallery, design, packages, ...) is fetched by
// i18next-http-backend on demand, the first time a page's own component
// calls useTranslation('thatNamespace') -- which lines up with when that
// page's own lazy-loaded chunk is requested (see App.js), so a route
// visit fetches its JS and its translations together.
//
// Earlier attempt: fetch *all* namespaces (including these 4) over HTTP,
// same as the rest. Measured regression: i18next always loads the
// fallback language's resources alongside the detected one, and neither
// fetch can start until main.js has downloaded *and executed* (the
// backend's request is issued by app code, not discoverable by the HTML
// preload scanner) -- that serializes a full network round-trip after
// the JS download instead of overlapping it. <link rel=preload> for the
// namespace files helps, but only for languages guessed correctly, and
// piling on preloads for all 3 languages measurably slowed down main.js
// itself on a bandwidth-throttled connection (they compete for the same
// pipe). Real-throttled Lighthouse LCP: ~4.6s (all-static, before this
// sprint) -> ~8.4s (all-lazy) -> back to parity once the first-paint
// namespaces were bundled synchronously again, as done here.
// These are a build-time copy of public/locales/{lng}/{ns}.json, synced by
// scripts/build/sync-critical-translations.js (wired into `prebuild`) because
// CRA's webpack config refuses to import anything from outside src/.
// public/locales/ remains the single source of truth -- don't hand-edit
// the files under src/locales/critical/, they're regenerated every build.
import enCommon from './locales/critical/en/common.json';
import enHome from './locales/critical/en/home.json';
import enHero from './locales/critical/en/hero.json';
import enCookieConsent from './locales/critical/en/cookieConsent.json';
import ruCommon from './locales/critical/ru/common.json';
import ruHome from './locales/critical/ru/home.json';
import ruHero from './locales/critical/ru/hero.json';
import ruCookieConsent from './locales/critical/ru/cookieConsent.json';
import fiCommon from './locales/critical/fi/common.json';
import fiHome from './locales/critical/fi/home.json';
import fiHero from './locales/critical/fi/hero.json';
import fiCookieConsent from './locales/critical/fi/cookieConsent.json';

// Sprint 8.1: when this HTML was produced by scripts/build/prerender.js, #root
// carries data-prerendered-lang="<locale>" -- the language the static
// markup was actually rendered in (see PRERENDER_LOCALE in
// scripts/build/prerender.js). The very first client render must use that exact
// language: if LanguageDetector picked a different one from this visitor's
// own localStorage/navigator.language, React would build its first tree in
// a different language than the HTML it's supposed to hydrate against.
// Passing an explicit `lng` here skips the detector for this resolution
// only; the visitor's real preference is applied afterwards as a separate,
// controlled update (see the post-hydration effect in App.js).
const prerenderedLang = document.getElementById('root')?.dataset.prerenderedLang;

const criticalResources = {
  en: { common: enCommon, home: enHome, hero: enHero, cookieConsent: enCookieConsent },
  ru: { common: ruCommon, home: ruHome, hero: ruHero, cookieConsent: ruCookieConsent },
  fi: { common: fiCommon, home: fiHome, hero: fiHero, cookieConsent: fiCookieConsent },
};

// Sprint 8.1: a prerendered page's *route-specific* namespace (e.g.
// 'projects', 'gallery') is normally fetched over HTTP the first time a
// component calls useTranslation() for it -- fine for normal browsing, but
// fatal for hydration. The very first client render happens before that
// fetch can resolve, so the Suspense boundary wrapping routes (App.js)
// would want to render its fallback (PageLoader) -- which doesn't match
// the real, fully-translated content already sitting in the prerendered
// HTML. That mismatch is a genuine hydration failure (React errors
// #418/#423), not a cosmetic one: it happened on every single prerendered
// route during testing, independent of language/theme/viewport.
// scripts/build/prerender.js embeds exactly the namespaces this route actually
// resolved (see its own comment) as inline JSON right before this script
// runs; reading it back here and merging it into `resources` makes those
// namespaces available synchronously, so nothing suspends during hydration.
let embeddedResources = {};
try {
  const el = document.getElementById('__PRERENDERED_I18N__');
  if (el) embeddedResources = JSON.parse(el.textContent);
} catch {
  embeddedResources = {};
}

const resources = { ...criticalResources };
if (prerenderedLang && Object.keys(embeddedResources).length > 0) {
  resources[prerenderedLang] = { ...resources[prerenderedLang], ...embeddedResources };
}

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    lng: prerenderedLang || undefined,
    // Without this, i18next's own default (['translation']) applies --
    // it tries to ensure that namespace is loaded on init regardless of
    // any useTranslation() call, and since no translation.json exists,
    // the backend fetched (and Suspense waited on) a 404 for it on every
    // single load.
    ns: ['common', 'home', 'hero', 'cookieConsent'],
    resources,
    // Let the backend keep serving every namespace *not* covered by the
    // static resources above (see i18next source: without this flag,
    // providing `resources` disables the backend entirely).
    partialBundledLanguages: true,
    defaultNS: 'common',
    fallbackLng: 'en',
    // Only 'en'/'ru'/'fi' resource directories exist (see public/locales).
    // Without this, a browser reporting e.g. navigator.language = 'ru-RU'
    // makes the backend request /locales/ru-RU/*.json first (404, one per
    // namespace) before falling back to /locales/ru/*.json.
    load: 'languageOnly',
    backend: {
      // Files live in public/locales/{lng}/{ns}.json -- served as static
      // assets, not bundled by webpack.
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    react: {
      // A component calling useTranslation('ns') for a namespace that
      // isn't loaded yet suspends until the backend fetches it, caught by
      // the same <Suspense> boundary already wrapping routes (App.js).
      // Namespaces already in `resources` above resolve immediately, no
      // suspending, no network dependency.
      useSuspense: true,
    },
  });

// Read back by scripts/build/prerender.js after a route finishes loading, to
// snapshot exactly the namespaces that route resolved (see the
// __PRERENDERED_I18N__ comment above). Not a security/privacy concern --
// i18n resources are static UI copy, not user data -- and unused by the
// app itself at runtime.
if (typeof window !== 'undefined') {
  window.__I18N_INSTANCE__ = i18n;
}

export default i18n;
